import React, { useCallback } from 'react';

import './FactionPlayerCard.css';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { formatThousands } from '@wwe-portal/utils/format';
import { cn } from '@wwe-portal/ui/lib/lib';
import PlayerRank from './PlayerRank';
import { PlayerInfo } from '../FactionTopPlayers/getTopPlayerListFromFaction';
import { getPlayerProfileImageUrl } from '@wwe-portal/web/utils/avatar';
import { slugify } from '@wwe-portal/web/utils/string';

export const avatarFrameRender = (frame) => {
  switch (frame) {
    case 'Rare':
      return '/assets/images/faction/BlueGlow.webp';
    case 'Epic':
      return '/assets/images/faction/PurpleGlow.webp';
    case 'Legendary':
      return '/assets/images/faction/OrangeGlow.webp';
    default:
      return '';
  }
};

type Props = {
  playerInfo: PlayerInfo;
  playerRank: number;
  wins: number;
  isStrongest: boolean;
};

function FactionPlayerCard({
  playerInfo,
  playerRank,
  wins,
  isStrongest,
}: Props) {
  const t = useTranslations();

  const renderFrame = useCallback(() => {
    if (playerInfo.frame !== null) {
      return playerInfo.frame;
    } else if (playerInfo.avatarRarity !== null) {
      return avatarFrameRender(playerInfo.avatarRarity);
    }
  }, []);

  const playerFrame = renderFrame();
  const imageUrl = getPlayerProfileImageUrl(
    playerInfo.userId,
    undefined,
    playerInfo.image,
    false
  );

  return (
    <div className="faction-player-card flex h-full flex-col items-center rounded-[10px] px-[15px] py-[10px] 2xl:px-[25px]">
      <div className="faction-player-card-image-container m-[30px] flex items-center justify-center rounded-full">
        <Link
          className="link-light text-decoration-none relative m-0"
          href={`/player/${slugify(playerInfo.name || '')}`}
          data-id={`faction_player_card_${playerInfo.name}`}
        >
          <div
            style={{
              backgroundImage: `url(${imageUrl})`,
            }}
            className="faction-player-card-image-container-image rounded-full"
          />

          {(playerInfo.frame !== '0' || playerInfo.avatarRarity !== '0') &&
          playerFrame ? (
            <img
              src={playerFrame}
              alt="frame"
              className="faction-player-card-image-container-frame max-w-none rounded-full"
            />
          ) : (
            ''
          )}
        </Link>
      </div>
      <h5 className="mb-[10px]">
        <Link
          className="link-light text-decoration-none m-0 pb-[10px]"
          href={`/player/${slugify(playerInfo.name || '')}`}
          data-id={`faction_player_card_player_name_${playerInfo.name}`}
        >
          {playerInfo.name}
        </Link>
      </h5>
      <p className="text-text600 m-0 pb-[30px] text-xs">
        {t('player_faction_rank')} :{' '}
        <span className="text-text500">
          {' '}
          {playerInfo.factionRank || 'unknown'}{' '}
        </span>
      </p>
      <hr className="faction-player-card-hr border-borderColor7 m-0 border" />
      <div className="faction-player-card-footer flex w-full items-center justify-between pb-[30px] pt-[30px]">
        <div className="faction-player-card-footer-first-half m-0">
          <PlayerRank rank={playerRank} title={t('rank_in_faction')} />
        </div>
        {!isStrongest || wins ? (
          <div className="faction-player-card-footer-second-half m-0">
            <p
              className={cn(
                'text-text400 m-0 pb-[5px] pl-[10px] pr-[10px] pt-[5px] text-sm font-semibold',
                {
                  'faction-player-card-footer-second-half-button rounded-[6px]':
                    wins >= 1000,
                  'faction-player-card-footer-second-half-rounded': wins < 1000,
                }
              )}
            >
              {formatThousands(wins)}
            </p>
            {!isStrongest ? (
              <p className="text-text600 m-0 p-0 pt-[10px] text-xs">
                {t('roster_talent')}
              </p>
            ) : (
              <p className="text-text600 m-0 p-0 pt-[10px] text-xs">
                {t('battle_wins')}
              </p>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default FactionPlayerCard;
